import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchMemberType,
  deleteMemberType,
  addMemberIndicator,
  updateMemberIndicator,
  deleteMemberIndicator,
  addMemberCriteria,
  deleteMemberCriteria,
  addMemberCriteriaValue,
  deleteMemberCriteriaValue,
} from "../../api/companyApi";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

const MEMBER_CLASS_LABELS = {
  Collaborator: "memberType.class.Collaborator",
  CompanyMember: "memberType.class.CompanyMember",
  Specialist: "memberType.class.Specialist",
  Investor: "memberType.class.Investor",
  ProductUser: "memberType.class.ProductUser",
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function MemberTypeDetailPage() {
  const { businessDomainId, memberTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // KPI state
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(-1);
  const [kpiForm, setKpiForm] = useState({
    indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "",
  });
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(-1);

  // Criteria state
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({
    classificationCriteriaCd: "", classificationCriteriaName: "",
  });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // Criteria Value state
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({
    classificationCriteriaValueCd: "", classificationCriteriaValueName: "",
  });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try { setData(await fetchMemberType(memberTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberTypeId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Delete member type ──
  const handleDeleteMemberType = async () => {
    try {
      await deleteMemberType(memberTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/member-types`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── KPI CRUD ──
  const openKpiAddDialog = () => {
    setKpiEditMode(false); setKpiEditIndex(-1);
    setKpiForm({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
    setKpiDialogOpen(true);
  };
  const openKpiEditDialog = (index, indicator) => {
    setKpiEditMode(true); setKpiEditIndex(index);
    setKpiForm({
      indicatorName: indicator.indicatorName || "", unit: indicator.unit || "",
      measuringMethod: indicator.measuringMethod || "", evaluationStandard: indicator.evaluationStandard || "",
      description: indicator.description || "",
    });
    setKpiDialogOpen(true);
  };
  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName || !kpiForm.unit || !kpiForm.description) { notify.error(t("message.error.required")); return; }
    try {
      if (kpiEditMode) {
        const previousIndicator = data.indicators[kpiEditIndex];
        await updateMemberIndicator(memberTypeId, {
          previousIndicator: {
            indicatorName: previousIndicator.indicatorName,
            unit: previousIndicator.unit,
            measuringMethod: previousIndicator.measuringMethod,
            evaluationStandard: previousIndicator.evaluationStandard,
            description: previousIndicator.description,
          },
          postIndicator: { ...kpiForm },
        });
      } else {
        if ((data.indicators || []).some((ind) => ind.indicatorName === kpiForm.indicatorName)) { notify.error(t("memberKpi.error.duplicate")); return; }
        await addMemberIndicator(memberTypeId, { ...kpiForm, createdAt: new Date().toISOString() });
      }
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    try {
      const indicator = data.indicators[kpiDeleteIndex];
      await deleteMemberIndicator(memberTypeId, {
        indicatorName: indicator.indicatorName,
        unit: indicator.unit,
        measuringMethod: indicator.measuringMethod,
        evaluationStandard: indicator.evaluationStandard,
        description: indicator.description,
      });
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setKpiConfirmOpen(false); setKpiDeleteIndex(-1);
  };

  // ── Criteria CRUD ──
  const openCriteriaAddDialog = () => {
    setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" });
    setCriteriaDialogOpen(true);
  };
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) { notify.error(t("message.error.required")); return; }
    try {
      await addMemberCriteria(memberTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success"));
      setCriteriaDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCriteriaDelete = async () => {
    try {
      await deleteMemberCriteria(memberTypeId, criteriaDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const openCvAddDialog = () => {
    setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
    setCvDialogOpen(true);
  };
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addMemberCriteriaValue(memberTypeId, {
        classificationCriteria: selectedCriteria,
        classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd,
        classificationCriteriaValueName: cvForm.classificationCriteriaValueName,
        createdValueAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      setCvDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCvDelete = async () => {
    try {
      await deleteMemberCriteriaValue(memberTypeId, cvDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  if (!data) return null;

  const memberClassLabel = MEMBER_CLASS_LABELS[data.memberClass] ? t(MEMBER_CLASS_LABELS[data.memberClass]) : data.memberClass || "";
  const indicators = data.indicators || [];
  const criterias = data.classificationCriterias || [];

  // Filter criteria values for the selected criteria
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter(
        (v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName
      )
    : [];

  const kpiColumns = [
    { field: "indicatorName", headerName: t("memberKpi.indicatorName"), flex: 1, minWidth: 150 },
    { field: "unit", headerName: t("memberKpi.unit"), width: 120 },
    { field: "description", headerName: t("memberKpi.description"), flex: 1, minWidth: 150, renderCell: wrapTextCell },
    { field: "actions", headerName: t("common.actions"), width: 120, sortable: false,
      renderCell: (p) => {
        const idx = indicators.findIndex((ind) => ind.indicatorName === p.row.indicatorName);
        return (<Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openKpiEditDialog(idx, p.row); }}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setKpiDeleteIndex(idx); setKpiConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
        </Box>);
      },
    },
  ];
  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("memberCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("memberCriteria.name"), flex: 1, minWidth: 200 },
    { field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setCriteriaDeleteTarget({ classificationCriteriaCd: p.row.classificationCriteriaCd, classificationCriteriaName: p.row.classificationCriteriaName });
          setCriteriaConfirmOpen(true);
        }}><DeleteIcon fontSize="small" /></IconButton>
      ),
    },
  ];
  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    { field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCvDeleteTarget(p.row); setCvConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("memberType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/design/${businessDomainId}/member-types/${memberTypeId}/edit`)}>{t("common.edit")}</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("memberType.name")} value={data.memberTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("memberType.memberClass")} value={memberClassLabel} /></Grid>
          <Grid size={12}><DetailField label={t("memberType.description")} value={data.memberTypeDescription} /></Grid>
          <Grid size={12}><DetailField label={t("memberType.values")} value={data.values} /></Grid>
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("memberKpi.title")} />
          <Tab label={t("memberCriteria.title")} />
        </Tabs>
      </Box>

      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("memberKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAddDialog}>{t("common.add")}</Button>
        </Box>
        <DataGrid rows={indicators} columns={kpiColumns} getRowId={(r) => r.indicatorName}
          key={indicators.map((i) => i.indicatorName).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick getRowHeight={() => 'auto'} />
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        {selectedCriteria ? (
          /* ── Criteria Value view ── */
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1">
                {selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCvAddDialog}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid
              rows={criteriaValues}
              columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              key={criteriaValues.map((v) => v.classificationCriteriaValueCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          /* ── Criteria list view ── */
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("memberCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCriteriaAddDialog}>{t("common.add")}</Button>
            </Box>
            <DataGrid rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              key={criterias.map((c) => c.classificationCriteriaCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <Box sx={{ mt: 3 }}><Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/member-types`)}>{t("common.back")}</Button></Box>

      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteMemberType} onCancel={() => setConfirmOpen(false)} />

      {/* KPI Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("memberKpi.edit.title") : t("memberKpi.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("memberKpi.indicatorName")} value={kpiForm.indicatorName} onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField fullWidth required multiline label={t("memberKpi.unit")} value={kpiForm.unit} onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField fullWidth multiline label={t("memberKpi.measuringMethod")} value={kpiForm.measuringMethod} onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField fullWidth multiline label={t("memberKpi.evaluationStandard")} value={kpiForm.evaluationStandard} onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField fullWidth required multiline label={t("memberKpi.description")} value={kpiForm.description} onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteIndex(-1); }} />

      {/* Criteria Add Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("memberCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("memberCriteria.code")} value={criteriaForm.classificationCriteriaCd} onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("memberCriteria.name")} value={criteriaForm.classificationCriteriaName} onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete} onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />

      {/* Criteria Value Add Dialog */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")}
              value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")}
              value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete} onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />
    </Box>
  );
}
